import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  Grid,
  IconButton,
} from "@mui/material";
import { ReloadButton } from "../../components";
import { hasBagsLoadedData, splitFlightCode } from "../../helpers";
import { SwapVert, Flight as FlightIcon } from "@mui/icons-material";

const RELOAD_TIMEOUT_SECONDS = 10;

const StatusLabel = ({ passengerCounts }) => {
  let label = "";
  let color = "gray";

  const {
    countAdultsCheckedIn,
    countInfantsCheckedIn,
    countAdultsBoarded,
    countInfantsBoarded,
  } = passengerCounts;

  const totalBoarded = countAdultsBoarded + countInfantsBoarded;
  const totalCheckedIn = countAdultsCheckedIn + countInfantsCheckedIn;

  if (totalBoarded === totalCheckedIn && totalCheckedIn > 0) {
    label = "Boarding completed";
    color = "green";
  } else if (totalBoarded > 0) {
    label = "In progress";
    color = "blue";
  } else {
    label = "Not started";
    color = "gray";
  }

  return (
    <Typography
      variant="body1"
      fontWeight="bold"
      component="span"
      sx={{ color }}
    >
      {label}
    </Typography>
  );
};

export const StatusCard = ({
  date,
  flightId,
  flightNumber,
  airport,
  boardPoint,
  offPoint,
  checkInInfo,
  checkInCompleted,
  boardingInfo,
  refreshSeatmapData,
  isLoading,
  totalPassengers,
  passengerCounts,
  bagList = {},
  showRotateButton = false,
  onRotateSeatMap = () => {},
  isSeatMapRotated = false,
  awaitedBagsLabel = null,
  totalInfants = 0,
  destinationCounts = null,
}) => {
  const {
    countAdultsCheckedIn,
    countInfantsCheckedIn,
    countAdultsBoarded,
    countInfantsBoarded,
    countInfants,
  } = passengerCounts;

  const { checkedInCount: bagsCheckedIn = 0, loadedCount: bagsLoaded = 0 } =
    bagList || {};
  const showBagsLoaded = hasBagsLoadedData(bagList);

  const totalInfantsCount =
    typeof totalInfants === "number" ? totalInfants : countInfants;
  const totalAdults = Math.max(totalPassengers - totalInfantsCount, 0);
  const totalBoardedAdults = countAdultsBoarded;
  const totalBoardedInfants = countInfantsBoarded;


  const splitFlightCodeArray = splitFlightCode(flightNumber);

  const [fetchedTime, setFetchedTime] = useState(new Date());
  const [timeLabel, setTimeLabel] = useState("");

  const updateLabel = () => {
    const now = new Date();
    const secondsElapsed = Math.floor((now - fetchedTime) / 1000);

    let label = `${secondsElapsed} seconds ago`;
    if (secondsElapsed >= 60) {
      const minutes = Math.floor(secondsElapsed / 60);
      label = `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
    }
    if (secondsElapsed >= 3600) {
      const hours = Math.floor(secondsElapsed / 3600);
      label = `${hours} hour${hours !== 1 ? "s" : ""} ago`;
    }
    if (secondsElapsed >= 86400) {
      const days = Math.floor(secondsElapsed / 86400);
      label = `${days} day${days !== 1 ? "s" : ""} ago`;
    }
    if (secondsElapsed >= 2592000) {
      const months = Math.floor(secondsElapsed / 2592000);
      label = `${months} month${months !== 1 ? "s" : ""} ago`;
    }
    if (secondsElapsed >= 31536000) {
      const years = Math.floor(secondsElapsed / 31536000);
      label = `${years} year${years !== 1 ? "s" : ""} ago`;
    }

    setTimeLabel(label);
  };

  useEffect(() => {
    const interval = setInterval(updateLabel, 1000);

    return () => clearInterval(interval);
  }, [fetchedTime]);

  const isReloadDisabled = (() => {
    const now = new Date();
    const secondsElapsed = Math.floor((now - fetchedTime) / 1000);
    return !refreshSeatmapData || secondsElapsed < RELOAD_TIMEOUT_SECONDS;
  })();

  const handleReload = async () => {
    if (!refreshSeatmapData) {
      console.error(
        "Cannot refresh seatmap data: refreshSeatmapData is not available"
      );
      return;
    }

    setFetchedTime(new Date());
    await refreshSeatmapData(
      flightId,
      splitFlightCodeArray?.flightPrefix,
      splitFlightCodeArray?.flightNumber,
      date,
      airport,
      boardPoint,
      offPoint
    );
  };

  return (
    <Card elevation={0}>
      <CardContent>
        <Grid container justifyContent="space-between" alignItems={"center"}>
          <Grid item>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {flightNumber}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1" color="textSecondary" align="right">
              {date}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="caption">{timeLabel}</Typography>
            <ReloadButton
              disabled={isReloadDisabled}
              onClick={handleReload}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        <Grid container direction="column" spacing={2}>
          {/* <Grid item> DISABLE PER NOREWEGIANS REQUEST
            <Box>
              <Typography
                variant='body1'
                component='span'
                sx={{ fontWeight: 'bold' }}
              >
                {'Check-in: '}
              </Typography>
              <StatusLabel completed={checkInCompleted} />
            </Box>
            <Typography variant='body2' color='textSecondary'>
              {checkInInfo}
            </Typography>
          </Grid> */}
          <Grid item>
            <Box>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {"Boarding: "}
              </Typography>
              <StatusLabel
                passengerCounts={passengerCounts}
                totalPassengers={totalPassengers}
              />
            </Box>
            <Typography component="span" variant="body2" color="textSecondary">
              {totalBoardedAdults}+{totalBoardedInfants} INF of
            </Typography>{" "}
            <Typography component="span" variant="body2" color="textSecondary">
              {totalAdults}+{totalInfantsCount} INF
            </Typography>
          </Grid>
          {destinationCounts && Object.keys(destinationCounts).length > 0 && (
            <Grid item>
              <Box>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{ fontWeight: "bold" }}
                >
                  {"PAX per destination: "}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5 }}
              >
                {Object.entries(destinationCounts)
                  .sort(
                    ([, a], [, b]) =>
                      b.adults + b.infants - (a.adults + a.infants)
                  )
                  .map(([dest, count]) => (
                    <Box
                      key={dest}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 152, 0, 0.15)",
                        borderRadius: "4px",
                        padding: "2px 8px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", color: "#e65100" }}
                      >
                        {dest}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ ml: 0.5 }}
                      >
                        {count.adults}
                        {count.infants > 0 ? ` + ${count.infants} INF` : ""}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Grid>
          )}
          <Grid item sx={{ marginTop: 1 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Box>
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{ fontWeight: "bold" }}
                  >
                    {"Bags checked-in: "}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="textSecondary"
                  >
                    {bagsCheckedIn}
                  </Typography>
                </Box>
                {showBagsLoaded && (
                <Box>
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{ fontWeight: "bold" }}
                  >
                    {"Bags loaded: "}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="textSecondary"
                  >
                      {bagsLoaded}
                  </Typography>
                </Box>
                )}
                {awaitedBagsLabel && (
                  <Box>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{ fontWeight: "bold" }}
                    >
                      {"Awaited passengers' bags: "}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textSecondary"
                    >
                      {awaitedBagsLabel}
                    </Typography>
                  </Box>
                )}
              </Box>

              {showRotateButton && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    color="primary"
                    aria-label="Rotate seat map"
                    onClick={onRotateSeatMap}
                    sx={{ padding: 0.5 }}
                  >
                    <SwapVert sx={{ color: "black" }} />
                  </IconButton>
                  <FlightIcon
                    sx={{
                      color: "grey",
                      fontSize: 24,
                      marginLeft: 0.5,
                      transform: isSeatMapRotated ? "rotate(180deg)" : "none",
                      transition: "transform 0.6s ease-in-out",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
